import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Avatar,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { openModal } from 'store/reducers/actions';
import FreeBlockModal from 'sections/premiumBlock/FreeBlockModal';
import Security from '@mui/icons-material/Security';
import { DownloadOutlined } from '@ant-design/icons';
import ChevronRight from '@mui/icons-material/ChevronRight';

const PersonaICPExtended = ({ selectedPersona }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const plan = useSelector((state) => state.proAccountReducer.plan);
  // const trialWasActivated = useSelector((state) => state.proAccountReducer.trial_was_activated);
  const isProUser = plan === 'Pro' || plan === 'Trial' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';
  const isModalOpen = useSelector((state) => state.modalReducer.isOpen);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // isProUser = false
  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Get access to detailed ICP analysis',
        subtitle: "Upgrade to Pro to view all ICP details. Don't miss out on accessing more insights!",
        trial: false
      })
    );
  };

  const handleDownloadClick = (event) => {
    if (!isProUser) {
      dispatch(
        openModal({
          title: 'Need to download ICP details?',
          subtitle: "Upgrade to Pro to download detailed ICP analysis. Don't miss out on accessing your insights!",
          trial: false
        })
      );
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadDOC = () => {
    const rtfContent = convertToRTF(persona);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${persona.name}_icp.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  if (!selectedPersona) return null;
  console.log(isProUser);
  const persona = selectedPersona || selectedPersona.description;

  const renderList = (items) => {
    // Handle case where items is not an array
    if (!Array.isArray(items)) {
      // If it's a string, just display it as text
      if (typeof items === 'string') {
        return (
          <Typography variant="body1" sx={{ pl: 0 }}>
            {items}
          </Typography>
        );
      }
      // If it's not an array or string, return null
      return null;
    }

    return (
      <List sx={{ pl: 0 }}>
        {items.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body1', component: 'span' }} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderChips = (items) => {
    // Handle case where items is not an array
    if (!Array.isArray(items)) {
      // If it's a string, just display it as text
      if (typeof items === 'string') {
        return <Typography variant="body1">{items}</Typography>;
      }
      // If it's not an array or string, return null
      return null;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((item, index) => (
          <Chip key={index} label={item} variant="outlined" size="small" />
        ))}
      </Box>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 9) return `${theme.palette.success.main}99`; // 60% opacity
    if (score >= 8) return `${theme.palette.success.light}99`;
    if (score >= 7) return `${theme.palette.warning.main}99`;
    return `${theme.palette.error.main}99`;
  };

  const renderScoreBar = (item) => (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="body1" sx={{ flex: 1 }}>
          {item.description}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: getScoreColor(item.final_score),
            ml: 2,
            fontWeight: 500
          }}
        >
          {item.final_score}/10
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={item.final_score * 10}
        sx={{
          height: 6,
          borderRadius: 1,
          bgcolor: theme.palette.secondary.light,
          '& .MuiLinearProgress-bar': {
            bgcolor: getScoreColor(item.final_score)
          }
        }}
      />
    </Box>
  );

  const renderBusinessObjectives = (objectives) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} whileHover={{ scale: 1.01 }}>
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          position: 'relative',
          isolation: 'isolate',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '1px',
            borderRadius: 'inherit',
            background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            zIndex: -1
          },
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
          }
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Business Objectives
        </Typography>
        {/* Show first item normally */}
        {renderScoreBar({
          description: `${Object.keys(objectives)[0]}: ${objectives[Object.keys(objectives)[0]].description}`,
          final_score: objectives[Object.keys(objectives)[0]].final_score
        })}

        {/* Blur remaining items with premium overlay */}
        {!isProUser && (
          <Box sx={{ position: 'relative', mt: 3 }}>
            <Box
              sx={{
                filter: 'blur(5px)',
                userSelect: 'none',
                opacity: 0.6
              }}
            >
              {Object.entries(objectives)
                .slice(1)
                .map(([key, value]) =>
                  renderScoreBar({
                    description: `${key}: ${value.description}`,
                    final_score: value.final_score
                  })
                )}
            </Box>
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Stack spacing={2} alignItems="center">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    justifyContent: 'center'
                  }}
                >
                  <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                    Business Objectives Details
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                  Start free trial to access all business objectives and understand your ICP&apos;s goals better.
                </Typography>
                <Box>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                    <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                      Start Free Trial
                    </Button>
                  </motion.div>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}
        {/* Show all items for pro users */}
        {isProUser &&
          Object.entries(objectives)
            .slice(1)
            .map(([key, value]) =>
              renderScoreBar({
                description: `${key}: ${value.description}`,
                final_score: value.final_score
              })
            )}
      </Box>
    </motion.div>
  );

  const renderGroupBlock = (title, items) => {
    // Add safety check to ensure items is an array
    const itemsArray = Array.isArray(items) ? items : [];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ scale: 1.01 }}
      >
        <Box
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
            position: 'relative',
            isolation: 'isolate',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '1px',
              borderRadius: 'inherit',
              background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              zIndex: -1
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
            }
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            {title}
          </Typography>

          {/* Blur all items with premium overlay for non-pro users */}
          {!isProUser && (
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  filter: 'blur(5px)',
                  userSelect: 'none',
                  opacity: 0.6
                }}
              >
                {itemsArray.map((item) => renderScoreBar(item))}
              </Box>
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      justifyContent: 'center'
                    }}
                  >
                    <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                      {title} Details
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                    Start free trial to access all {title.toLowerCase()} and understand your ICP&apos;s{' '}
                    {title === 'Success Metrics'
                      ? 'success factors'
                      : title === 'Pain Points'
                      ? 'challenges'
                      : title === 'Obstacles'
                      ? 'barriers'
                      : title === 'Needs'
                      ? 'requirements'
                      : 'details'}{' '}
                    better.
                  </Typography>
                  <Box>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                      <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                        Start Free Trial
                      </Button>
                    </motion.div>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
          {/* Show all items for pro users */}
          {isProUser && itemsArray.map((item) => renderScoreBar(item))}
        </Box>
      </motion.div>
    );
  };

  const BlurredChips = ({ chips, handleTrialClick }) => {
    const chipsArray = Array.isArray(chips) ? chips : [];

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {chipsArray.map((item, index) => (
          <Box key={index} sx={{ position: 'relative' }}>
            <Chip
              label={
                <Box
                  sx={{
                    filter: 'blur(5px)',
                    userSelect: 'none',
                    minWidth: '60px',
                    opacity: 0.06
                  }}
                >
                  {item}
                </Box>
              }
              variant="outlined"
              size="small"
              onClick={handleTrialClick}
              sx={{
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                pointerEvents: 'none' // Let clicks go through to the chip
              }}
            >
              <VisibilityIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6 }} />
              <Typography variant="caption">View</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderRightColumnChips = (chips) => (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          filter: 'blur(5px)',
          userSelect: 'none',
          opacity: 0.6
        }}
      >
        {renderChips(chips)}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          opacity: 0.5,
          cursor: 'pointer',
          '&:hover': {
            opacity: 1
          }
        }}
        onClick={handleTrialClick}
      >
        <VisibilityIcon fontSize="small" />
        <Typography variant="caption" color="black" ml={1}>
          View
        </Typography>
      </Box>
    </Box>
  );

  const convertToRTF = (persona) => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Basic Info
    rtf += `\\b\\fs32 ${escapeRTF(persona.name)}\\b0\\fs20\\par\n`;
    rtf += `${escapeRTF(persona.age)} - ${escapeRTF(persona.gender)} - ${escapeRTF(persona.location)}\\par\n`;
    rtf += `${escapeRTF(persona.education)} - ${escapeRTF(persona.familyStatus)}\\par\\par\n`;

    // Professional Summary
    rtf += `\\b\\fs28 Professional Summary\\b0\\fs20\\par\n`;
    rtf += `${escapeRTF(persona.professionalSummary)}\\par\\par\n`;

    // Professional Background
    rtf += `\\b\\fs28 Professional Background\\b0\\fs20\\par\n`;
    rtf += `\\b Industry:\\b0 ${escapeRTF(persona.professionalBackground.industry)}\\par\n`;
    rtf += `\\b Company Size:\\b0 ${escapeRTF(persona.professionalBackground.companySize)}\\par\n`;
    rtf += `\\b Job Title:\\b0 ${escapeRTF(persona.professionalBackground.jobTitle)}\\par\n`;
    rtf += `\\b Experience:\\b0 ${escapeRTF(persona.professionalBackground.experience)}\\par\n`;
    rtf += `\\b Key Responsibilities:\\b0\\par\n`;
    persona.professionalBackground.keyResponsibilities.forEach((resp) => {
      rtf += `- ${escapeRTF(resp)}\\par\n`;
    });
    rtf += `\\par\n`;
    // Personal Profile
    rtf += `\\b\\fs28 Personal Profile\\b0\\fs20\\par\n`;
    rtf += `\\b Personality Traits:\\b0\\par\n`;
    persona.extendedPsychographics.personalityTraits.forEach((trait) => {
      rtf += `- ${escapeRTF(trait)}\\par\n`;
    });
    rtf += `\\par\\b Lifestyle:\\b0\\par\n`;
    rtf += `${escapeRTF(persona.extendedPsychographics.lifestyleRoutine)}\\par\\par\n`;
    rtf += `\\b Hobbies & Interests:\\b0\\par\n`;
    persona.extendedPsychographics.hobbiesAndInterests.forEach((hobby) => {
      rtf += `- ${escapeRTF(hobby)}\\par\n`;
    });
    rtf += `\\par\n`;
    // Extended Career
    rtf += `\\b\\fs28 Career Details\\b0\\fs20\\par\n`;
    rtf += `\\b Seniority Level:\\b0 ${escapeRTF(persona.extendedCareer.seniorityLevel)}\\par\n`;
    rtf += `\\b Personal Goals:\\b0\\par\n`;
    persona.extendedCareer.personalGoals.forEach((goal) => {
      rtf += `- ${escapeRTF(goal)}\\par\n`;
    });
    rtf += `\\b Professional Goals:\\b0\\par\n`;
    persona.extendedCareer.professionalGoals.forEach((goal) => {
      rtf += `- ${escapeRTF(goal)}\\par\n`;
    });
    rtf += `\\b Industry Impact Goals:\\b0 ${escapeRTF(persona.extendedCareer.industryImpactGoals)}\\par\\par\n`;

    // Goals and Objectives
    rtf += `\\b\\fs28 Goals & Objectives\\b0\\fs20\\par\n`;
    rtf += `\\b Primary Goals:\\b0 ${escapeRTF(persona.goalsAndObjectives.primaryGoals)}\\par\\par\n`;

    rtf += `\\b Business Objectives:\\b0\\par\n`;
    Object.entries(persona.goalsAndObjectives.businessObjectives).forEach(([key, obj]) => {
      rtf += `- ${escapeRTF(key)}: ${escapeRTF(obj.description)} (Score: ${obj.final_score}/10)\\par\n`;
    });
    rtf += `\\par\n`;

    rtf += `\\b Success Metrics:\\b0\\par\n`;
    persona.goalsAndObjectives.successMetrics.forEach((metric) => {
      rtf += `- ${escapeRTF(metric.description)}: ${metric.final_score}/10\\par\n`;
    });
    rtf += `\\par\n`;

    // Challenges and Pain Points
    rtf += `\\b\\fs28 Challenges & Pain Points\\b0\\fs20\\par\n`;
    rtf += `\\b Pain Points:\\b0\\par\n`;
    persona.challengesAndPainPoints.painPoints.forEach((point) => {
      rtf += `- ${escapeRTF(point.description)}: ${point.final_score}/10\\par\n`;
    });
    rtf += `\\b Obstacles:\\b0\\par\n`;
    persona.challengesAndPainPoints.obstacles.forEach((obstacle) => {
      rtf += `- ${escapeRTF(obstacle.description)}: ${obstacle.final_score}/10\\par\n`;
    });
    rtf += `\\b Needs:\\b0\\par\n`;
    persona.challengesAndPainPoints.needs.forEach((need) => {
      rtf += `- ${escapeRTF(need.description)}: ${need.final_score}/10\\par\n`;
    });
    rtf += `\\par\n`;

    // Values and Motivations
    rtf += `\\b\\fs28 Values & Motivations\\b0\\fs20\\par\n`;
    rtf += `\\b Core Values:\\b0\\par\n`;
    persona.valuesAndMotivations.coreValues.forEach((value) => {
      rtf += `- ${escapeRTF(value)}\\par\n`;
    });
    rtf += `\\b Motivators:\\b0 ${escapeRTF(persona.valuesAndMotivations.motivators)}\\par\n`;
    rtf += `\\b Aspirations:\\b0 ${escapeRTF(persona.valuesAndMotivations.aspirations)}\\par\\par\n`;

    // Buying Decision Process
    rtf += `\\b\\fs28 Buying Decision Process\\b0\\fs20\\par\n`;
    rtf += `\\b Decision Making Style:\\b0 ${escapeRTF(persona.buyingDecisionProcess.decisionMakingStyle)}\\par\n`;
    rtf += `\\b Primary Motivations:\\b0\\par\n`;
    persona.buyingDecisionProcess.primaryMotivations.forEach((motivation) => {
      rtf += `- ${escapeRTF(motivation)}\\par\n`;
    });
    rtf += `\\b Barriers to Purchase:\\b0\\par\n`;
    persona.buyingDecisionProcess.barriersToPurchase.forEach((barrier) => {
      rtf += `- ${escapeRTF(barrier)}\\par\n`;
    });
    rtf += `\\b Valued Features:\\b0\\par\n`;
    persona.buyingDecisionProcess.valuedFeatures.forEach((feature) => {
      rtf += `- ${escapeRTF(feature)}\\par\n`;
    });
    rtf += `\\b Preferred Competitors:\\b0\\par\n`;
    persona.buyingDecisionProcess.preferredCompetitors.forEach((competitor) => {
      rtf += `- ${escapeRTF(competitor)}\\par\n`;
    });
    rtf += `\\b Likelihood to Recommend:\\b0 ${escapeRTF(persona.buyingDecisionProcess.likelihoodToRecommend)}\\par\\par\n`;

    // Digital Presence
    rtf += `\\b\\fs28 Digital Presence\\b0\\fs20\\par\n`;
    rtf += `\\b Social Media Platforms:\\b0\\par\n`;
    persona.extendedDigital.socialMediaPlatforms.forEach((platform) => {
      rtf += `- ${escapeRTF(platform)}\\par\n`;
    });
    rtf += `\\b Online Communities:\\b0\\par\n`;
    persona.extendedDigital.onlineCommunities.forEach((community) => {
      rtf += `- ${escapeRTF(community)}\\par\n`;
    });
    rtf += `\\b Followed Influencers:\\b0\\par\n`;
    persona.extendedDigital.followedInfluencers.forEach((influencer) => {
      rtf += `- ${escapeRTF(influencer)}\\par\n`;
    });
    rtf += `\\b Preferred Content Formats:\\b0\\par\n`;
    persona.extendedDigital.preferredContentFormats.forEach((format) => {
      rtf += `- ${escapeRTF(format)}\\par\n`;
    });
    rtf += `\\par\n`;

    // Technology Usage
    rtf += `\\b\\fs28 Technology Stack\\b0\\fs20\\par\n`;
    Object.entries(persona.behaviorsAndHabits.technologyUsage).forEach(([category, tools]) => {
      rtf += `\\b ${escapeRTF(category)}:\\b0\\par\n`;
      if (Array.isArray(tools)) {
        tools.forEach((tool) => {
          rtf += `- ${escapeRTF(tool)}\\par\n`;
        });
      } else {
        rtf += `- ${escapeRTF(tools)}\\par\n`;
      }
    });
    rtf += `\\par\n`;

    // Communication Preferences
    rtf += `\\b\\fs28 Communication Preferences\\b0\\fs20\\par\n`;
    rtf += `\\b Preferred Channels:\\b0\\par\n`;
    persona.preferredCommunicationChannels.channels.forEach((channel) => {
      rtf += `- ${escapeRTF(channel)}\\par\n`;
    });
    rtf += `\\b Content Preferences:\\b0\\par\n`;
    persona.preferredCommunicationChannels.contentPreferences.forEach((preference) => {
      rtf += `- ${escapeRTF(preference)}\\par\n`;
    });

    rtf += '}';
    return rtf;
  };

  return (
    <>
      {!isProUser && isModalOpen && <FreeBlockModal />}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 8 }}>
        {/* Header with download button */}
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item>
            <Avatar src={persona.image_url} sx={{ width: 100, height: 100 }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              {persona.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {persona.age} • {persona.gender} • {persona.location}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {persona.education} • {persona.familyStatus}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={!isProUser ? 'Upgrade to Pro to export' : 'Download'}>
              <IconButton onClick={handleDownloadClick}>
                <DownloadOutlined />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }}
            >
              <MenuItem onClick={downloadDOC}>Download as .doc</MenuItem>
            </Menu>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 4 }} />

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Professional Summary */}
            <Typography variant="h5" color="primary" gutterBottom>
              Professional Summary
            </Typography>
            <Typography variant="body1" paragraph>
              {persona.professionalSummary}
            </Typography>

            {/* Professional Background */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Professional Background
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Industry:</strong> {persona.professionalBackground?.industry}
                <br />
                <strong>Company Size:</strong> {persona.professionalBackground.companySize}
                <br />
                <strong>Job Title:</strong> {persona.professionalBackground.jobTitle}
                <br />
                <strong>Experience:</strong> {persona.professionalBackground.experience}
              </Typography>
              <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                Key Responsibilities:
              </Typography>
              {renderList(persona.professionalBackground.keyResponsibilities)}
            </Box>

            {/* Extended Career Section */}
            {(persona.extendedCareer?.seniorityLevel ||
              persona.extendedCareer?.personalGoals ||
              persona.extendedCareer?.professionalGoals ||
              persona.extendedCareer?.industryImpactGoals) && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Career Details
                </Typography>
                {persona.extendedCareer?.seniorityLevel && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Seniority Level:</strong> {persona.extendedCareer.seniorityLevel}
                  </Typography>
                )}

                <Grid container spacing={3}>
                  {persona.extendedCareer?.personalGoals && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Personal Goals:
                      </Typography>
                      {isProUser ? (
                        renderList(persona.extendedCareer.personalGoals)
                      ) : (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            {renderList(persona.extendedCareer.personalGoals)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: 1,
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  )}
                  {persona.extendedCareer?.professionalGoals && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Professional Goals:
                      </Typography>
                      {isProUser ? (
                        renderList(persona.extendedCareer.professionalGoals)
                      ) : (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            {renderList(persona.extendedCareer.professionalGoals)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: 1,
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  )}
                </Grid>

                {persona.extendedCareer?.industryImpactGoals && (
                  <Typography variant="body1" component="div" sx={{ mt: 2 }}>
                    <strong>Industry Impact Goals:</strong>{' '}
                    {isProUser ? (
                      persona.extendedCareer.industryImpactGoals
                    ) : (
                      <Box component="span" sx={{ position: 'relative', display: 'inline-block' }}>
                        <Box
                          component="span"
                          sx={{
                            filter: 'blur(5px)',
                            userSelect: 'none',
                            opacity: 0.6,
                            display: 'inline-block'
                          }}
                        >
                          {persona.extendedCareer.industryImpactGoals}
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            opacity: 0.5,
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: 1,
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                          onClick={handleTrialClick}
                        >
                          <VisibilityIcon fontSize="small" />
                          <Typography variant="caption" color="black" ml={1}>
                            View
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Typography>
                )}
              </Box>
            )}

            {/* Extended Psychographics Section */}
            {(persona.extendedPsychographics?.personalityTraits ||
              persona.extendedPsychographics?.lifestyleRoutine ||
              persona.extendedPsychographics?.hobbiesAndInterests) && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Personal Profile
                </Typography>
                {!isProUser ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      border: '1px dashed',
                      borderColor: 'primary.light',
                      borderRadius: 2,
                      bgcolor: 'rgba(0,118,255,0.03)'
                    }}
                  >
                    <Stack spacing={2} alignItems="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          justifyContent: 'center'
                        }}
                      >
                        <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                          Personal Profile Details
                        </Typography>
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                        Start free trial to access detailed persona insights, including professional background, career details, and more.
                      </Typography>
                      <Box>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                          <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                            Start Free Trial
                          </Button>
                        </motion.div>
                      </Box>
                    </Stack>
                  </Box>
                ) : (
                  <>
                    {persona.extendedPsychographics?.personalityTraits && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Personality Traits:
                        </Typography>
                        {renderChips(persona.extendedPsychographics.personalityTraits)}
                      </Box>
                    )}
                    {persona.extendedPsychographics?.lifestyleRoutine && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Lifestyle:
                        </Typography>
                        <Typography variant="body1">{persona.extendedPsychographics.lifestyleRoutine}</Typography>
                      </Box>
                    )}
                    {persona.extendedPsychographics?.hobbiesAndInterests && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Hobbies & Interests:
                        </Typography>
                        {renderChips(persona.extendedPsychographics.hobbiesAndInterests)}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Goals and Challenges */}
            <Box sx={{ mt: 2 }}>
              {/* Goals & Objectives */}
              <Typography variant="h5" color="primary" gutterBottom>
                Goals & Objectives
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Primary Goal:</strong> {persona.goalsAndObjectives.primaryGoals}
              </Typography>

              {/* Business Objectives Block */}
              <Box sx={{ mb: 4 }}>
                {/* <Typography variant="h5" sx={{ mb: 1 }}>
                                    Business Objectives:
                                </Typography> */}
                {renderBusinessObjectives(persona.goalsAndObjectives.businessObjectives)}
              </Box>

              {/* Success Metrics Block */}
              {renderGroupBlock('Success Metrics', persona.goalsAndObjectives.successMetrics)}

              {/* Challenges & Pain Points */}
              <Typography variant="h5" color="primary" gutterBottom>
                Challenges & Pain Points
              </Typography>

              {/* Pain Points Block */}
              {renderGroupBlock('Pain Points', persona.challengesAndPainPoints.painPoints)}

              {/* Obstacles Block */}
              {renderGroupBlock('Obstacles', persona.challengesAndPainPoints.obstacles)}

              {/* Needs Block */}
              {renderGroupBlock('Needs', persona.challengesAndPainPoints.needs)}
            </Box>

            {/* Professional Aspirations */}
            {persona.professionalAspirations && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Professional Aspirations
                </Typography>
                {isProUser ? (
                  <Typography variant="body1">{persona.professionalAspirations}</Typography>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      <Typography variant="body1">{persona.professionalAspirations}</Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={handleTrialClick}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="caption" color="black" ml={1}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Values and Motivations */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Values & Motivations
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Core Values:
              </Typography>
              {isProUser ? (
                renderChips(persona.valuesAndMotivations.coreValues)
              ) : (
                <BlurredChips chips={persona.valuesAndMotivations.coreValues} handleTrialClick={handleTrialClick} />
              )}

              {persona.valuesAndMotivations.motivators && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Motivators:
                  </Typography>
                  {isProUser ? (
                    <Typography variant="body1">{persona.valuesAndMotivations.motivators}</Typography>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        <Typography variant="body1">{persona.valuesAndMotivations.motivators}</Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {persona.valuesAndMotivations.aspirations && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Aspirations:
                  </Typography>
                  {isProUser ? (
                    <Typography variant="body1">{persona.valuesAndMotivations.aspirations}</Typography>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        <Typography variant="body1">{persona.valuesAndMotivations.aspirations}</Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Buying Behavior */}
            {persona.behaviorsAndHabits?.buyingBehavior && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Buying Behavior
                </Typography>
                {isProUser ? (
                  <Typography variant="body1">{persona.behaviorsAndHabits.buyingBehavior}</Typography>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      <Typography variant="body1">{persona.behaviorsAndHabits.buyingBehavior}</Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={handleTrialClick}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="caption" color="black" ml={1}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Customer Journey */}
            {persona.extendedBrandInteraction?.customerJourneyStages && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Customer Journey
                </Typography>
                {isProUser ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                    {Object.keys(persona.extendedBrandInteraction.customerJourneyStages).map((stage, idx, arr) => (
                      <React.Fragment key={stage}>
                        <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
                        <span style={{ fontWeight: 'bold' }}>
                          <strong>{stage}:</strong>
                        </span>{' '}
                        {persona.extendedBrandInteraction.customerJourneyStages[stage]}
                        {idx !== arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </Typography>
                ) : (
                  <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                    {Object.keys(persona.extendedBrandInteraction.customerJourneyStages).map((stage, idx, arr) => (
                      <React.Fragment key={stage}>
                        <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
                        <span style={{ fontWeight: 'bold' }}>
                          <strong>{stage}:</strong>
                        </span>{' '}
                        <Box component="span" sx={{ position: 'relative', display: 'inline-block' }}>
                          <Box
                            component="span"
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            {persona.extendedBrandInteraction.customerJourneyStages[stage]}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                        {idx !== arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                pl: 3,
                borderLeft: '2px solid',
                borderLeftColor: 'primary.main',
                height: '100%'
              }}
            >
              {/* Financial Profile */}
              {persona.financialProfile || persona.extendedFinancial ? (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Financial Profile
                  </Typography>
                  {(() => {
                    const financial = persona.financialProfile || persona.extendedFinancial || {};
                    const items = [
                      { key: 'Household Income', value: financial.householdIncome },
                      { key: 'Avg. Spend', value: financial.avgSpend || financial.averageSpendPerPurchase },
                      { key: 'Purchase Frequency', value: financial.purchaseFrequency },
                      { key: 'Price Sensitivity', value: financial.priceSensitivity }
                    ];
                    return (
                      <Typography variant="body1" component="div">
                        {items.map((item, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            {isProUser ? (
                              <Typography>
                                <strong>{item.key}:</strong> {item.value || '—'}
                              </Typography>
                            ) : (
                              <>
                                <Typography sx={{ display: 'block' }}>
                                  <strong>{item.key}:</strong>
                                </Typography>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, pl: 2, mt: 0.5 }}>
                                  <Box
                                    sx={{
                                      filter: 'blur(5px)',
                                      userSelect: 'none',
                                      opacity: 0.6
                                    }}
                                  >
                                    ********
                                  </Box>
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      cursor: 'pointer',
                                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                      opacity: 0.5,
                                      padding: '2px 8px',
                                      borderRadius: 1,
                                      whiteSpace: 'nowrap',
                                      transition: 'opacity 0.2s',
                                      '&:hover': {
                                        opacity: 1
                                      }
                                    }}
                                    onClick={handleTrialClick}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                    <Typography variant="caption" color="black" ml={1}>
                                      View
                                    </Typography>
                                  </Box>
                                </Box>
                              </>
                            )}
                          </Box>
                        ))}
                      </Typography>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Financial Profile
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    No financial profile data available.
                  </Typography>
                </Box>
              )}

              {/* Digital Presence */}
              {persona.extendedDigital && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Digital Presence
                  </Typography>
                  {persona.extendedDigital.socialMediaPlatforms && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Social Media:
                      </Typography>
                      {isProUser
                        ? renderChips(persona.extendedDigital.socialMediaPlatforms)
                        : renderRightColumnChips(persona.extendedDigital.socialMediaPlatforms)}
                    </Box>
                  )}
                  {persona.extendedDigital.onlineCommunities && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Communities:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.extendedDigital.onlineCommunities}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.extendedDigital.onlineCommunities}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              transition: 'opacity 0.2s',
                              '&:hover': { opacity: 1 }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  {persona.extendedDigital.followedInfluencers && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Followed Influencers:
                      </Typography>
                      {isProUser
                        ? renderChips(persona.extendedDigital.followedInfluencers)
                        : renderRightColumnChips(persona.extendedDigital.followedInfluencers)}
                    </Box>
                  )}
                  {persona.extendedDigital.preferredContentFormats && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Content Preferences:
                      </Typography>
                      {isProUser
                        ? renderChips(persona.extendedDigital.preferredContentFormats)
                        : renderRightColumnChips(persona.extendedDigital.preferredContentFormats)}
                    </Box>
                  )}
                </Box>
              )}

              {/* Technology Stack */}
              <Typography variant="h5" color="primary" gutterBottom>
                Technology Stack
              </Typography>
              {Object.entries(persona.behaviorsAndHabits.technologyUsage).map(([category, tools]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {category.charAt(0).toUpperCase() +
                      category
                        .slice(1)
                        .replace(/([A-Z])/g, ' $1')
                        .trim()}
                    :
                  </Typography>
                  {isProUser ? (
                    renderChips(Array.isArray(tools) ? tools : [tools])
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        {renderChips(Array.isArray(tools) ? tools : [tools])}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}

              {/* Media Consumption - in the right column */}
              {persona.behaviorsAndHabits?.mediaConsumption && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Media Consumption
                  </Typography>
                  {isProUser ? (
                    renderList(persona.behaviorsAndHabits.mediaConsumption)
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        {renderList(persona.behaviorsAndHabits.mediaConsumption)}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Decision Criteria */}
              {persona.objectionsAndDecisionCriteria && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Decision Making
                  </Typography>
                  {/* Common Objections */}
                  {persona.objectionsAndDecisionCriteria.commonObjections && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Common Objections:
                      </Typography>
                      {isProUser ? (
                        renderList(persona.objectionsAndDecisionCriteria.commonObjections)
                      ) : (
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%'
                          }}
                        >
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            {renderList(persona.objectionsAndDecisionCriteria.commonObjections)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  {persona.objectionsAndDecisionCriteria.decisionFactors && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Decision Factors:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.objectionsAndDecisionCriteria.decisionFactors}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.objectionsAndDecisionCriteria.decisionFactors}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              transition: 'opacity 0.2s',
                              '&:hover': { opacity: 1 }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  {persona.objectionsAndDecisionCriteria.influencers && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Key Influencers:
                      </Typography>
                      {isProUser
                        ? renderChips(persona.objectionsAndDecisionCriteria.influencers)
                        : renderRightColumnChips(persona.objectionsAndDecisionCriteria.influencers)}
                    </Box>
                  )}
                </Box>
              )}

              {/* Communication Preferences */}
              <Box sx={{ my: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Communication Preferences
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Preferred Channels:
                </Typography>
                {isProUser
                  ? renderChips(persona.preferredCommunicationChannels.channels)
                  : renderRightColumnChips(persona.preferredCommunicationChannels.channels)}

                {persona.preferredCommunicationChannels.contentPreferences && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Content Preferences:
                    </Typography>
                    {isProUser
                      ? renderChips(persona.preferredCommunicationChannels.contentPreferences)
                      : renderRightColumnChips(persona.preferredCommunicationChannels.contentPreferences)}
                  </Box>
                )}
              </Box>

              {/* Brand Interaction & Loyalty */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" color="primary" gutterBottom>
                  Brand Interaction
                </Typography>
                {/* Discovery Channel */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Discovery Channel:
                  </Typography>
                  {isProUser ? (
                    <Typography variant="body1">{persona.extendedBrandInteraction.discoveryChannel}</Typography>
                  ) : (
                    <Box sx={{ position: 'relative', mt: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        {persona.extendedBrandInteraction.discoveryChannel}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          padding: '2px 8px',
                          borderRadius: 1,
                          whiteSpace: 'nowrap',
                          transition: 'opacity 0.2s',
                          '&:hover': { opacity: 1 }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                {/* Brand Loyalty */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Brand Loyalty:
                  </Typography>
                  {isProUser ? (
                    <Typography variant="body1">{persona.extendedBrandInteraction.brandLoyalty}</Typography>
                  ) : (
                    <Box sx={{ position: 'relative', mt: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        {persona.extendedBrandInteraction.brandLoyalty}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          padding: '2px 8px',
                          borderRadius: 1,
                          whiteSpace: 'nowrap',
                          transition: 'opacity 0.2s',
                          '&:hover': { opacity: 1 }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                {persona.extendedBrandInteraction.conversionTriggers && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Conversion Triggers:
                    </Typography>
                    {isProUser ? (
                      <Typography variant="body1">{persona.extendedBrandInteraction.conversionTriggers}</Typography>
                    ) : (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            filter: 'blur(5px)',
                            userSelect: 'none',
                            opacity: 0.6
                          }}
                        >
                          <Typography variant="body1">{persona.extendedBrandInteraction.conversionTriggers}</Typography>
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            opacity: 0.5,
                            cursor: 'pointer',
                            padding: '2px 8px',
                            borderRadius: 1,
                            whiteSpace: 'nowrap',
                            transition: 'opacity 0.2s',
                            '&:hover': { opacity: 1 }
                          }}
                          onClick={handleTrialClick}
                        >
                          <VisibilityIcon fontSize="small" />
                          <Typography variant="caption" color="black" ml={1}>
                            View
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Buying Decision Process */}
              {persona.buyingDecisionProcess && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Buying Decision Process
                  </Typography>

                  {persona.buyingDecisionProcess.primaryMotivations && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Primary Motivations:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.buyingDecisionProcess.primaryMotivations}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.buyingDecisionProcess.primaryMotivations}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              transition: 'opacity 0.2s',
                              '&:hover': { opacity: 1 }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {persona.buyingDecisionProcess.barriersToPurchase && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Barriers to Purchase:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.buyingDecisionProcess.barriersToPurchase}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.buyingDecisionProcess.barriersToPurchase}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              transition: 'opacity 0.2s',
                              '&:hover': { opacity: 1 }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {persona.buyingDecisionProcess.decisionMakingStyle && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Decision Making Style:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.buyingDecisionProcess.decisionMakingStyle}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.buyingDecisionProcess.decisionMakingStyle}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {persona.buyingDecisionProcess.preferredCompetitors && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Preferred Competitors:
                      </Typography>
                      {isProUser
                        ? renderChips(persona.buyingDecisionProcess.preferredCompetitors)
                        : renderRightColumnChips(persona.buyingDecisionProcess.preferredCompetitors)}
                    </Box>
                  )}

                  {persona.buyingDecisionProcess.valuedFeatures && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Valued Features:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.buyingDecisionProcess.valuedFeatures}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.buyingDecisionProcess.valuedFeatures}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              transition: 'opacity 0.2s',
                              '&:hover': { opacity: 1 }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {persona.buyingDecisionProcess.likelihoodToRecommend && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Likelihood to Recommend:
                      </Typography>
                      {isProUser ? (
                        <Typography variant="body1">{persona.buyingDecisionProcess.likelihoodToRecommend}</Typography>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            <Typography variant="body1">{persona.buyingDecisionProcess.likelihoodToRecommend}</Typography>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default PersonaICPExtended;
